import { Dispatch, SetStateAction, useMemo } from "react"
import { Box, Button, Card, Link, useTheme } from "@mui/material"
import { getKitImage, getLastRefillDate, getPendingApproveLink, hasBeenRequested, hasRefillApprovalAccess } from "./utils/helpers"
import CompliancePlipLight from "../../../../components/CompliancePlipLight"
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import WarningIcon from '@mui/icons-material/Warning'
import LoopIcon from "@mui/icons-material/Loop"
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import PersonIcon from '@mui/icons-material/Person'
import CompliancePlipGroup from "../../../../components/CompliancePlipGroup"
import KitHeaderItem from "./KitHeaderItem"
import { User } from "../../../../redux/user"
import { Kit, KitItem, OpenModal } from "./types"
import { useNavigate } from "react-router-dom"
import { getComplianceColor } from "../../../../helpers/compliance"
import useIsCorpView from "../../../../hooks/useIsCorpView"
import { RefillFrequency } from "./enums"

type KitHeader = {
  kit: Kit,
  kitItems: KitItem[],
  isUpdatingItems: boolean,
  defaultIcon: string,
  setOpenModal: Dispatch<SetStateAction<OpenModal>>,
  currentUser: User | null,
}

const KitHeader = (props: KitHeader) => {
  const {
    kit,
    kitItems,
    isUpdatingItems,
    defaultIcon,
    setOpenModal,
    currentUser,
  } = props

  const theme = useTheme()
  const navigate = useNavigate()

  const isCorpView = useIsCorpView()

  const toggleActive = () => {}

  const openKitBuilder = (type: RefillFrequency) => {
    setOpenModal({ open: true, type })
  }

  const hasKitBeenRequested = useMemo(() => {
    return hasBeenRequested(kit)
  }, [kit])

  const complianceStatuses = useMemo(() => {
    const initialStatus = { 1: 0, 2: 0, 3: 0, 4: 0 }

    return kitItems.reduce((acc: Record<number, number>, item) => {
      acc[item.complianceStatus]++
      return acc
    }, initialStatus)
  }, [kitItems])

  const complianceMessages = useMemo(() => {
    return new Map([
      [3, 'Action Required'],
      [2, 'Attention Needed'],
      [1, 'Up To Date'],
      [4, 'Inactive'],
    ])
  }, [kitItems])

  const handleApproveRefill = () => {
    const link = getPendingApproveLink(kit, isCorpView);
    navigate(link);
  }

  return (
    <Card className="flex flex-align-center" sx={{ p: 2, color: theme.palette.color50555b.main, columnGap: '64px', flexWrap: 'wrap' }} variant="outlined">
      {
        Boolean(kitItems.length)
          ? (
              <>
                <Box className="flex flex-align-center" sx={{columnGap: '32px'}}>
                  <img
                    alt="kit"
                    className="m-l-16"
                    src={getKitImage(kit.image || defaultIcon)}
                    style={{ width: kit.image ? '100px' : '75px', objectFit: 'contain' }}
                  />
                  <Box>
                    <Box className="flex flex-align-center c-g-8">
                      <CompliancePlipLight medium={true} status={kit.complianceStatus} message={kit.messageShort} />
                      <span className="f-s-17 m-r-8">
                        { kit.model ?? '' }
                      </span>
                    </Box>
                    <Box className="f-s-13">
                      <Box>Kit #: { kit.serialNumber }</Box>
                      {
                        kit.location
                          && <Box>Location: {kit.location}</Box>
                      }
                      {
                        kit.lastRefillDate
                          && <Box>Last refilled in { getLastRefillDate(kit) }</Box>
                      }
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  {
                    hasKitBeenRequested
                      && (
                          <Box className="flex flex-end m-b-16">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={true}
                              sx={{
                                "&.Mui-disabled": {
                                  backgroundColor: "#27c24c",
                                  color: theme.palette.common.white
                                }
                              }}                      
                            >
                              <PriorityHighIcon sx={{ color: theme.palette.common.white }} fontSize="small" />
                              Refill Requested
                            </Button>
                          </Box>
                        )
                  }
                  <KitHeaderItem>
                    <>
                      <WarningIcon className={getComplianceColor(kit.complianceStatus)} fontSize="medium" />
                      { kit.messageLong }                    
                    </>
                  </KitHeaderItem>
                  {
                    kit.refillStatus === 1
                      && (
                          <KitHeaderItem>
                            <>
                              <LoopIcon className="color-success" fontSize="medium" />
                              <span className="color-success">Automatic Refill On</span>                            
                            </>
                          </KitHeaderItem>
                        )
                  }
                  {
                    kit.refillStatus === 4
                      && (
                          <KitHeaderItem>
                            <>
                              <LoopIcon className="color-success" fontSize="medium" />
                              <span className="color-success">On Membership</span>                            
                            </>
                          </KitHeaderItem>
                        )
                  }
                  {
                    kit.refillStatus === 2
                      && (
                          <KitHeaderItem>
                            <>
                              <LoopIcon className={kit.pendingApproval ? 'color-danger' : 'color-warning'} fontSize="medium" />
                              <span className={kit.pendingApproval ? 'color-danger' : 'color-warning'}>Refill on Approval</span>
                              {
                                kit.pendingApproval && hasRefillApprovalAccess(currentUser)
                                  && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        sx={{
                                          "&.MuiButton-contained": {
                                            backgroundColor: kit.complianceStatus === 2 ? "#f4b122" : "#f05050",
                                            color: theme.palette.common.white
                                          }
                                        }}
                                        onClick={handleApproveRefill}
                                      >
                                        Review and approve refill
                                      </Button>
                                    )
                              }                              
                              {
                                !kit.pendingApproval && hasRefillApprovalAccess(currentUser)
                                  && (
                                    <Button size="small" variant="contained" disabled={true}>
                                      Nothing To Approve
                                    </Button>
                                  )
                              }
                            </>
                          </KitHeaderItem>
                        )
                  }
                  {
                    !kit.pendingApproval && !hasKitBeenRequested && Boolean(kitItems.length)
                      && (
                        <>
                          {
                            kit.refillStatus === 0
                              && (
                                  <KitHeaderItem>
                                    <>
                                      <LoopIcon className="color-inactive" fontSize="medium" />
                                      <span className="color-inactive">Automatic Refill Off</span>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        sx={{ backgroundColor: theme.palette.success.main, ':hover': { backgroundColor: theme.palette.success.dark } }}
                                        onClick={() => openKitBuilder(RefillFrequency.Recurring)}
                                      >
                                        Set Up Automatic Refill
                                      </Button>
                                    </>
                                  </KitHeaderItem>
                                )
                          }
                          {
                            kit.refillStatus === 2 && [2,3].includes(kit.complianceStatus)
                              && (
                                  <KitHeaderItem>
                                    <>
                                      <ArrowCircleUpIcon className="color-success" fontSize="medium" />
                                      <Button
                                        size="small"
                                        variant="contained"
                                        sx={{ backgroundColor: theme.palette.success.main, ':hover': { backgroundColor: theme.palette.success.dark } }}
                                        onClick={() => openKitBuilder(RefillFrequency.Recurring)}
                                      >
                                        Upgrade To Automatic Refill
                                      </Button>
                                    </>
                                  </KitHeaderItem>
                                )
                          }
                          {
                            [2,3].includes(kit.complianceStatus)
                              && ((kit.refillStatus === 0 && kit.active) || kit.refillStatus === 2)
                              && (
                                  <KitHeaderItem>
                                    <>
                                    <ShoppingCartIcon className="color-success" fontSize="medium" />
                                      <Button
                                        size="small"
                                        variant="contained"
                                        sx={{ backgroundColor: theme.palette.success.main, ':hover': { backgroundColor: theme.palette.success.dark } }}
                                        onClick={() => openKitBuilder(RefillFrequency.OneTime)}
                                      >
                                        Re-Order Expired/Expiring items
                                      </Button>                            
                                    </>
                                  </KitHeaderItem>
                                )
                          }
                          {
                            kit.refillStatus === 0 && kit.active && [2,3].includes(kit.complianceStatus)
                              && (
                                  <KitHeaderItem>
                                    <span>
                                      <Link className="pointer" onClick={toggleActive}>Click here</Link>
                                      {' if you no longer have this kit'}
                                    </span>
                                  </KitHeaderItem>
                                )
                          }                        
                        </>
                      )
                  }
                  {
                    hasKitBeenRequested
                      && (
                          <KitHeaderItem>
                            <>
                              <PersonIcon sx={{ color: theme.palette.complianceBlue.main}} />
                              <span>You have requested a refill for this kit. A customer service representative will contact you shortly to confirm your order.</span>                            
                            </>
                          </KitHeaderItem>
                        )
                  }
                  {
                    isUpdatingItems === false && (
                      <Box className="flex flex-end flex-align-center m-t-16">
                        <CompliancePlipGroup
                          complianceStatuses={complianceStatuses}
                          complianceMessages={complianceMessages}
                        />
                      </Box>
                    )
                  }
                </Box>
              </>
            )
          : (
              <Box>
                <span>No items recorded yet for this kit</span>
              </Box>
            )
      }
    </Card>
  )
}

export default KitHeader
